import { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  PermissionEnum,
  Popup,
  Tabs,
  SidePanelLoader,
  ResponseError,
  responseErrorsResolver,
  ChangeLogs,
  PermissionDenied,
  getResponseErrors,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { RateRequestFormValues } from './types';
import RateRequestForm from '../../components/rate-request/form';
import {
  useLazyGetRateRequestItemQuery,
  useChangeRateRequestStatusMutation,
  useUpdateRateRequestsPairMutation,
  useLazyGetRateRequestChangeLogsQuery,
} from './store/services';
import { clearRateRequestDetailsState } from './store/slices/sidepanel';

import ROUTES from '../../routes';

import cn from './rate-request.module.css';

const RateRequestItem = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'log'>('details');
  const [globalError, setGlobalError] = useState('');

  const {
    exchangeDictionaries: { activePermissions, currencies },
    rateRequest: {
      sidepanel: { rateRequestDetails, changeLogs, changeLogsPagination },
    },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const { currency, rateId } = useParams();

  const {
    control,
    reset,
    formState: { errors, isDirty },
    watch,
    setValue,
    handleSubmit,
    setError,
  } = useForm<RateRequestFormValues>();

  const currencyFrom = watch('exchangeCurrencyFromId');
  const status = watch('active');

  const fields = watch();

  const [getDetails, { isLoading, isFetching }] =
    useLazyGetRateRequestItemQuery();
  const [changeStatus, { isLoading: changeStatusInProgress, isError }] =
    useChangeRateRequestStatusMutation();
  const [update, { isLoading: requestInProgress }] =
    useUpdateRateRequestsPairMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetRateRequestChangeLogsQuery();

  const navigate = useNavigate();
  const { search } = useLocation();

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=EXCHANGE_CUSTOM_RATE_RULE&entityId=${rateId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [rateId],
  );

  const onSubmit = handleSubmit(async (values) => {
    const response = (await update({
      id: rateId as string,
      body: {
        maxDeviationPercent: Number(values.maxDeviationPercent),
      },
    })) as {
      error: ResponseError | { data: { message: string } };
      data?: { data: RateRequestFormValues };
    };

    if (response) {
      if (response?.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError,
        });
      } else {
        reset({
          ...fields,
          maxDeviationPercent: values.maxDeviationPercent,
        });
      }
    }
  });

  const handleClose = () => {
    setPanelIsOpen(false);
    setDirtyPopupVisible(false);
    reset({});
    dispatch(clearRateRequestDetailsState());

    const url = generatePath(ROUTES.RATE_REQUEST_CURRENCY, {
      currency: currency as string,
    });

    setTimeout(() => {
      navigate(`${url}${search || ''}`);
    }, 100);
  };

  const onStatusChange = (active: boolean) => {
    setValue('active', active);

    changeStatus({
      id: rateId as string,
      active,
    });
  };

  const getDetailsAction = useCallback(async (id: string) => {
    const response = (await getDetails(id)) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList: setGlobalError,
      });
    }
  }, []);

  useEffect(() => {
    if (isError) {
      setValue('active', !status);
    }
  }, [isError, status]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (rateId) {
      getDetailsAction(rateId);
    }
  }, [rateId]);

  useEffect(() => {
    if (rateRequestDetails) {
      reset({
        active: rateRequestDetails.active,
        exchangeCurrencyFromId: rateRequestDetails.exchangeCurrencyFromId,
        exchangeCurrencyToId: rateRequestDetails.exchangeCurrencyToId,
        maxDeviationPercent: rateRequestDetails.maxDeviationPercent,
      });
    }
  }, [rateRequestDetails]);

  useEffect(() => {
    if (
      activeTab === 'log' &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  let content;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        <div className={cn.settingsTabsWrapper}>
          <Tabs
            tabs={[
              {
                name: 'Details',
                value: 'details',
              },
              {
                name: 'Change log',
                value: 'log',
              },
            ]}
            activeTab={activeTab}
            onClick={(tab) => setActiveTab(tab as 'details' | 'log')}
          />
        </div>
        {activeTab === 'details' && (
          <RateRequestForm
            control={control}
            errors={errors}
            currencies={currencies}
            currencyFrom={currencyFrom}
            rateId={rateId}
            isEditable={Boolean(
              activePermissions?.includes(
                PermissionEnum.SETTINGS_CUSTOM_RATE_EXCHANGE_UPDATE_ACTION,
              ),
            )}
            changeStatusInProgress={changeStatusInProgress}
            onStatusChange={onStatusChange}
          />
        )}
        {activeTab === 'log' && (
          <>
            {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <ChangeLogs
                entityType="EXCHANGE_CUSTOM_RATE_RULE"
                entityId={rateId as string}
                changeLogsIsLoading={changeLogsIsLoading}
                changeLogs={changeLogs}
                activePermission={activePermissions}
                users={users}
                pagination={changeLogsPagination}
                changeLogsIsFetching={changeLogsIsFetching}
                getChangeLogs={getChangeLogsAction}
              />
            )}
            {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <PermissionDenied />
            )}
          </>
        )}
      </>
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        errorSidepanel={globalError}
        sidepanelExpandedKey="rate-request-panel"
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        title={
          isLoading || isFetching
            ? 'Loading...'
            : `${
                currencies?.find(
                  (item) =>
                    item.id === rateRequestDetails?.exchangeCurrencyFromId,
                )?.description
              } - ${
                currencies?.find(
                  (item) =>
                    item.id === rateRequestDetails?.exchangeCurrencyToId,
                )?.description
              }`
        }
        className={cn.rateDetailsSidepanel}
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_CUSTOM_RATE_EXCHANGE_UPDATE_ACTION,
            ) && (
              <ButtonPrimary
                text={requestInProgress ? 'Loading...' : 'Save'}
                onClick={onSubmit}
                disabled={!isDirty || requestInProgress}
                bold
              />
            )}

            <ButtonSecondary
              text="Close"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              className="button-white"
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default RateRequestItem;
